import React, { useEffect, useMemo, useRef, useState } from 'react';
import { get, set, has, omit, isEmpty } from 'lodash/fp';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  FormControl,
  Form,
  Icon,
  SelectPicker,
  ButtonToolbar,
  RadioGroup,
  Radio,
  HelpBlock
} from 'rsuite';
import {
  fetchROItemOptions,
  fetchReservationTrackingMethodOptions
} from '../../../redux/Reservation/sagas/routines';
import { getAll as getROItemOptions } from '../../../redux/Reservation/selectors/ro-item-options';
import { getOrderedOptions as getTrackinMethodOptions } from '../../../redux/Reservation/selectors/tracking-method-options';
import { PicklistType } from '../../../interfaces/reservation';
import { composeLimitRulesMap } from '../../../helpers/limitRules';
import { ControlLabel, FormGroup } from '../../widgets/FormElements';
import LimitRulesLabel from '../LimitRulesLabel';
import LimitRulesModal from '../LimitRulesModal';
import CategoriesFormGroup from '../CategoriesFormGroup';
import {
  FormModel,
  EmptyCategoryValue,
  DefaultFormValue,
  picklistToFormValue,
  filterNonExistingItemOptions
} from './settings';

const ROPicklistForm = ({ data, isSaving, onSubmit }) => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const isCreate = !get('id', data);
  const [formError, setFormError] = useState({});
  const [formValue, setFormValue] = useState(DefaultFormValue);
  const [defaultCategory, setDefaultCategory] = useState(EmptyCategoryValue);
  const [showLimitRules, setShowLimitRules] = useState(false);
  const [limitRulesMap, setLimitRulesMap] = useState({});
  const itemOptions = useSelector(getROItemOptions);
  const trackingMethodOptions = useSelector(getTrackinMethodOptions);
  const { limitRules, categories } = formValue;

  const filteredItemOptions = useMemo(
    () => filterNonExistingItemOptions(itemOptions, categories),
    [categories, itemOptions]
  );

  useEffect(() => {
    dispatch(fetchROItemOptions());
    dispatch(fetchReservationTrackingMethodOptions());
    if (data) {
      setFormValue(picklistToFormValue(data));
    }
  }, []);

  useEffect(
    () => {
      setLimitRulesMap(composeLimitRulesMap(limitRules, categories));
    },
    [limitRules, categories]
  );

  const handleIsVisibleChange = isVisible => {
    setFormValue({ ...formValue, isVisible });
  };

  const handleShowLimitRules = () => {
    setShowLimitRules(true);
  };

  const handleHideLimitRules = () => {
    setShowLimitRules(false);
  };

  const handleSaveLimitRules = value => {
    setFormValue({ ...formValue, limitRules: value });
    setShowLimitRules(false);
    if (has('limitRules', formError)) {
      setFormError(omit('limitRules', formError));
    }
  };

  const removeLimitRules = () => {
    setFormValue({ ...formValue, limitRules: null });
  };

  const handleChangeCategories = value => {
    const updatedCategories = has('id', formValue)
      ? value.map((item, i) => ({ ...item, position: i + 1 }))
      : value;
    setFormValue(set('categories', updatedCategories, formValue));

    if (has('categories', formError)) {
      setFormError(omit('categories', formError));
    }
  };

  const handleFormChange = value => {
    setFormValue(value);
  };

  const handleFormCheck = errors => {
    setFormError(errors);
  };

  const handleCategoriesFormCheck = errors => {
    if (!isEmpty(errors)) setFormError({ ...formError, categories: errors });
    else if (has('categories', formError))
      setFormError(omit('categories', formError));
  };

  const handleSubmit = () => {
    if (!formRef.current.check()) {
      return;
    }
    onSubmit(formValue);
  };

  const handleReset = () => {
    if (isCreate) {
      setFormValue(DefaultFormValue);
      setDefaultCategory({ ...defaultCategory });
    } else {
      setFormValue(picklistToFormValue(data));
    }
  };

  return (
    <Form
      ref={formRef}
      model={FormModel}
      layout="horizontal"
      formValue={formValue}
      formError={formError}
      onCheck={handleFormCheck}
      onChange={handleFormChange}
      style={{ maxWidth: 850 }}
    >
      <FormGroup>
        <ControlLabel>Name *</ControlLabel>
        <FormControl
          name="name"
          errorMessage={formError.name}
          placeholder="Pick List Name"
        />
      </FormGroup>
      <FormGroup>
        <ControlLabel>Tracking Method Link *</ControlLabel>
        <FormControl
          name="trackingMethodId"
          accepter={SelectPicker}
          placeholder="Select Tracking Method"
          style={{ width: 300, marginTop: 10 }}
          data={trackingMethodOptions}
        />
      </FormGroup>
      <FormGroup>
        <ControlLabel>Description</ControlLabel>
        <FormControl
          name="description"
          rows={3}
          componentClass="textarea"
          placeholder="Pick List Description"
          errorMessage={formError.description}
        />
      </FormGroup>
      <FormGroup>
        <ControlLabel>Comment Label *</ControlLabel>
        <FormControl
          name="notes"
          rows={3}
          componentClass="textarea"
          placeholder="Provide comments or instructions for this Pick List"
          errorMessage={formError.notes}
        />
      </FormGroup>
      <FormGroup>
        <ControlLabel>Priority</ControlLabel>
        <FormControl
          name="position"
          type="number"
          min={1}
          max={1000}
          placeholder="Priority order"
          errorMessage={formError.position}
        />
        <HelpBlock style={{ marginLeft: 182 }}>
          Set Pick List priority order. 1 is 1st, 2 is 2nd and so on... <br />
          Note: When value is the same, it will be order by name.
        </HelpBlock>
      </FormGroup>
      <FormGroup controlId="isVisible">
        <ControlLabel>Status</ControlLabel>
        <RadioGroup
          inline
          name="isVisible"
          value={formValue.isVisible}
          onChange={handleIsVisibleChange}
        >
          <Radio value>Show</Radio>
          <Radio value={false}>Hide</Radio>
        </RadioGroup>
      </FormGroup>

      <FormGroup>
        <ControlLabel>Limit Rules</ControlLabel>
        <div>
          {limitRules && (
            <LimitRulesLabel rules={limitRules} style={{ marginRight: 10 }} />
          )}
          <Button size="sm" onClick={handleShowLimitRules}>
            <Icon icon="pencil" />
            {!limitRules ? ' Create' : ' Edit'}
          </Button>
          {limitRules && (
            <Button
              size="sm"
              color="red"
              onClick={removeLimitRules}
              style={{ marginLeft: 5 }}
            >
              <Icon icon="close-circle" />
              &nbsp;Remove
            </Button>
          )}
        </div>
        <FormControl
          type="hidden"
          name="limitRules"
          value={limitRules ? JSON.stringify(limitRules) : ''}
          errorMessage={get('limitRules', formError)}
        />
        <HelpBlock style={{ marginLeft: 182 }}>
          Pick List Rules is holding the Client choices to a max total,
          <br />
          independant from category and item rules
        </HelpBlock>
      </FormGroup>
      <CategoriesFormGroup
        name="categories"
        data={categories}
        defaultValue={defaultCategory}
        onChange={handleChangeCategories}
        onCheck={handleCategoriesFormCheck}
        limitRulesMap={limitRulesMap}
        itemOptions={filteredItemOptions}
      />
      <ButtonToolbar style={{ textAlign: 'right', marginTop: 30 }}>
        <Button onClick={handleReset} appearance="default" disabled={isSaving}>
          <Icon icon="refresh" /> Reset
        </Button>
        <Button
          onClick={handleSubmit}
          appearance="primary"
          disabled={isSaving || !isEmpty(formError)}
        >
          <Icon icon="save" /> {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </ButtonToolbar>
      <LimitRulesModal
        show={showLimitRules}
        rules={limitRules}
        onSave={handleSaveLimitRules}
        onCancel={handleHideLimitRules}
      />
    </Form>
  );
};

ROPicklistForm.propTypes = {
  data: PicklistType,
  onSubmit: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired
};

ROPicklistForm.defaultProps = {
  data: null
};

export default ROPicklistForm;
